import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
    SubscriptionPlan,
    PaymentInitializeRequest,
    PaymentInitializeResponse,
    PaymentVerifyRequest,
    PaymentVerifyResponse,
    SubscriptionResponse,
    PaymentHistoryItem,
    UpgradeTierRequest,
    CancelSubscriptionRequest,
} from '@/types/subscription'

interface GetSubscriptionResponse {
    success: boolean
    message: string
    data: SubscriptionResponse
}

interface GetPlansResponse {
    success: boolean
    message: string
    data: { plans: SubscriptionPlan[] } | SubscriptionPlan[]
}

interface InitializePaymentResponse {
    success: boolean
    message: string
    data: PaymentInitializeResponse
}

interface VerifyPaymentResponse {
    success: boolean
    message: string
    data: PaymentVerifyResponse
}

interface UpgradeTierResponse {
    success: boolean
    message: string
    data: { subscription: SubscriptionResponse }
}

interface CancelSubscriptionResponse {
    success: boolean
    message: string
    data: { message: string }
}

interface PaymentHistoryResponse {
    success: boolean
    message: string
    data: {
        payments: PaymentHistoryItem[]
        totalCount: number
        currentPage: number
        totalPages: number
    }
}

export const subscriptionRtkApi = createApi({
    reducerPath: 'subscriptionRtkApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${(import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api$/, '')}/api/v1`,
        credentials: 'include',
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('token')
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }
            return headers
        },
    }),
    tagTypes: ['Subscription', 'PaymentHistory', 'Plans'],
    endpoints: (builder) => ({
        getPlans: builder.query<SubscriptionPlan[], void>({
            query: () => '/subscriptions/plans',
            transformResponse: (response: GetPlansResponse) => {
                if (Array.isArray(response.data)) {
                    return response.data
                }
                if (Array.isArray(response.data?.plans)) {
                    return response.data.plans
                }
                return []
            },
            providesTags: ['Plans'],
            keepUnusedDataFor: 300,
        }),

        getCurrentSubscription: builder.query<SubscriptionResponse | null, void>({
            query: () => '/subscriptions/current',
            transformResponse: (response: GetSubscriptionResponse) => response.data,
            providesTags: ['Subscription'],
            keepUnusedDataFor: 30,
        }),

        getPaymentHistory: builder.query<
            {
                payments: PaymentHistoryItem[]
                totalCount: number
                currentPage: number
                totalPages: number
            },
            { page?: number; limit?: number }
        >({
            query: ({ page = 1, limit = 10 }) =>
                `/subscriptions/history?page=${page}&limit=${limit}`,
            transformResponse: (response: PaymentHistoryResponse) => response.data,
            providesTags: ['PaymentHistory'],
            keepUnusedDataFor: 60,
        }),

        initializePayment: builder.mutation<
            PaymentInitializeResponse,
            PaymentInitializeRequest
        >({
            query: (data) => ({
                url: '/subscriptions/initialize',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: InitializePaymentResponse) => response.data,
            invalidatesTags: ['Subscription'],
        }),

        verifyPayment: builder.mutation<
            PaymentVerifyResponse,
            PaymentVerifyRequest
        >({
            query: (data) => ({
                url: '/subscriptions/verify',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: VerifyPaymentResponse) => response.data,
            invalidatesTags: ['Subscription', 'PaymentHistory'],
        }),

        upgradeTier: builder.mutation<
            SubscriptionResponse,
            UpgradeTierRequest
        >({
            query: (data) => ({
                url: '/subscriptions/upgrade',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: UpgradeTierResponse) => response.data.subscription,
            invalidatesTags: ['Subscription', 'PaymentHistory'],
        }),

        cancelSubscription: builder.mutation<
            { message: string },
            CancelSubscriptionRequest
        >({
            query: (data) => ({
                url: '/subscriptions/cancel',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: CancelSubscriptionResponse) => response.data,
            invalidatesTags: ['Subscription', 'PaymentHistory'],
        }),
    }),
})

export const {
    useGetPlansQuery,
    useGetCurrentSubscriptionQuery,
    useGetPaymentHistoryQuery,
    useInitializePaymentMutation,
    useVerifyPaymentMutation,
    useUpgradeTierMutation,
    useCancelSubscriptionMutation,
} = subscriptionRtkApi
